import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SplitSection, EditorialIntro } from '../innovher/SplitSection';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';

const PULSE_IMAGES = [
  {
    src: '/_Q9A8049.jpg.webp',
    title: 'Operating rhythm',
    tag: 'Clarity',
  },
  {
    src: '/new_images/innovpulse-team-1.webp',
    title: 'Leadership review',
    tag: 'Alignment',
  },
  {
    src: '/new_images/innovpulse-team-2.webp',
    title: 'Cross-functional discussion',
    tag: 'Execution',
  },
  {
    src: '/new_images/Copy of IMGL4119.webp',
    title: 'Shared priorities',
    tag: 'Ownership',
  },
  {
    src: '/new_images/Copy of IMGL4180.webp',
    title: 'Strategy in motion',
    tag: 'Performance',
  },
  {
    src: '/new_images/IMG_0582.webp',
    title: 'Team accountability',
    tag: 'Follow-through',
  },
];

const ENABLES = [
  {
    title: 'Priority & performance clarity',
    body: 'Connect personal and team goals with clear progress and performance visibility.',
  },
  {
    title: 'Execution tracking',
    body: 'Follow key actions, priorities and deliverables across functions through a structured operating rhythm.',
  },
  {
    title: 'Cross-functional alignment',
    body: 'Create visibility across interdependent teams so shared priorities move forward with clear ownership.',
  },
  {
    title: 'Organisational visibility',
    body: 'Give leadership and teams a consolidated view of execution, progress and performance across the organisation.',
  },
  {
    title: 'Accountability & ownership',
    body: 'Make responsibilities, priorities and deliverables easy to assign, track and follow through.',
  },
  {
    title: 'Structured reviews',
    body: 'Support consistent performance and progress reviews with a shared view of outcomes, not fragmented updates.',
  },
];

const LOOP = ['Align', 'Execute', 'Measure', 'Improve'];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function EnablesInteractive() {
  const [active, setActive] = useState(0);
  const item = ENABLES[active];

  return (
    <div className="mt-10 grid lg:grid-cols-[0.95fr_1.05fr] gap-5 lg:gap-10 items-start">
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {ENABLES.map((e, i) => {
          const on = i === active;
          return (
            <button
              key={e.title}
              type="button"
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left rounded-2xl border px-4 py-3.5 transition-all ${on
                ? 'border-[#d42b92]/60 bg-[#d42b92]/12'
                : 'border-transparent hover:border-white/10 hover:bg-white/[0.02]'
                }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`text-[11px] font-semibold tracking-[0.2em] shrink-0 ${on ? 'text-[#ff8ec5]' : 'text-white/30'
                    }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className={`text-sm sm:text-base font-medium ${on ? 'text-white' : 'text-white/60'}`}>
                  {e.title}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div
        key={item.title}
        className="relative rounded-[1.5rem] sm:rounded-[1.75rem] border border-white/10 overflow-hidden min-h-[260px]"
      >
        <img
          src={PULSE_IMAGES[active % PULSE_IMAGES.length].src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/30" />
        <div className="relative p-6 sm:p-10 flex flex-col justify-end min-h-[260px]">
          <div className="text-[#ff8ec5] text-xs tracking-[0.28em] uppercase mb-3">
            What InnovPulse enables
          </div>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white leading-tight">
            {item.title}
          </h3>
          <p className="mt-4 text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed">
            {item.body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function InnovPulsePage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="InnovPulse | Organisational Operating and Performance System"
        description="InnovPulse connects organisational priorities, ownership, execution and performance in one structured operating framework inside the InnovHer ecosystem."
        keywords="InnovPulse, operating system, performance management, organisational clarity, InnovHer, KPI, execution tracking"
      />

      {/* Header / Hero Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="InnovPulse"
            logo="/new_images/logos/innovpulse.webp"
            title={
              <>
                One system. One view.{' '}
                <span className="text-[#d42b92]">Better execution</span>.
              </>
            }
            lead="An integrated operating and performance system that connects organisational priorities, ownership, execution and performance."
          >
            <p>
              InnovPulse is an integrated operating and performance system that connects
              organisational priorities, ownership, execution and performance in one structured
              framework.
            </p>
            <p>
              It gives teams and leadership a unified view of how work moves across functions, so
              alignment, accountability and delivery stay clear as the organisation grows.
            </p>
          </EditorialIntro>
        </div>
      </section>

      {/* Built for organisational clarity */}
      <section className="relative bg-[#08080a] py-14 sm:py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[320px] sm:w-[420px] h-[320px] sm:h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="reveal-up order-2 lg:order-1 grid grid-cols-2 gap-3">
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[3/4] group">
              <img
                src={PULSE_IMAGES[1].src}
                alt="InnovPulse team member working on laptop"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                style={{ objectPosition: '75% 15%' }}
                loading="lazy"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[3/4] mt-8 group">
              <img
                src={PULSE_IMAGES[2].src}
                alt="InnovPulse team member coordinating"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </div>
          <div className="reveal-up order-1 lg:order-2 max-w-xl">
            <SectionEyebrow>Built for Organisational Clarity</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
              Visibility and coordination as teams{' '}
              <span className="text-[#d42b92]">scale</span>
            </h2>
            <div className="mt-5 sm:mt-6 space-y-4 sm:space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>As teams scale, visibility and coordination become harder to maintain.</p>
              <p>
                InnovPulse brings individual responsibilities, team objectives and shared priorities
                onto a common platform. Teams and leaders can stay aligned on progress, dependencies
                and outcomes, without losing sight of the larger organisational goals.
              </p>
              <p className="text-white font-medium">
                The system supports disciplined execution while keeping strategy and day-to-day work
                connected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What it enables */}
      <section className="relative bg-[#08080a] py-14 sm:py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <SplitSection
            className="reveal-up"
            eyebrow="What InnovPulse Enables"
            title={
              <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
                Clarity across priorities, ownership and{' '}
                <span className="text-[#d42b92]">delivery</span>
              </h2>
            }
          >
            {/* <p className="text-whi/te/45 text-sm">Hover or select a capability to explore.</p> */}
          </SplitSection>
          <div className="reveal-up">
            <EnablesInteractive />
          </div>
        </div>
      </section>

      {/* Atmosphere gallery */}
      <section className="relative bg-[#08080a] py-14 sm:py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-4xl lg:max-w-5xl mb-10">
            <SectionEyebrow>Where execution stays visible</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
              Built for teams that need to stay{' '}
              <span className="text-[#d42b92]">aligned</span>
            </h2>
          </div>
          <div className="reveal-up grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {PULSE_IMAGES.map((img) => (
              <div
                key={img.src}
                className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]"
              >
                <img
                  src={img.src}
                  alt={img.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {/* <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff8ec5] mb-1">{img.tag}</div>
                  <div className="text-sm text-white/90 font-medium">{img.title}</div>
                </div> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* From strategy to execution */}
      <section className="relative bg-[#08080a] py-14 sm:py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="reveal-up max-w-xl">
            <SectionEyebrow>From Strategy to Execution</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
              Direction into measurable{' '}
              <span className="text-[#d42b92]">action</span>
            </h2>
            <div className="mt-5 sm:mt-6 space-y-4 sm:space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>InnovPulse helps turn organisational direction into measurable action.</p>
              <p>
                By connecting goals, ownership and execution, it creates a clearer way to manage
                performance and strengthen operational discipline across teams.
              </p>
            </div>
          </div>
          <div className="reveal-up rounded-[1.5rem] sm:rounded-[1.75rem] overflow-hidden border border-white/10 aspect-[4/3]">
            <img
              src={PULSE_IMAGES[4].src}
              alt="Strategy to execution"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 mt-10">
          <div className="reveal-up grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
            {LOOP.map((step, i) => (
              <div
                key={step}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 sm:px-5 py-5 sm:py-6 text-center"
              >
                <div className="text-[#ff8ec5] text-[10px] sm:text-[11px] tracking-[0.25em] uppercase mb-2">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="text-lg sm:text-xl md:text-2xl font-semibold text-white">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Close */}
      <section className="relative bg-[#08080a] py-14 sm:py-16 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up rounded-[1.5rem] sm:rounded-[1.75rem] border border-white/10 overflow-hidden max-w-5xl">
            <div className="relative p-6 sm:p-10 lg:p-14 min-h-[280px] flex flex-col justify-end">
              <img
                src={PULSE_IMAGES[3].src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
              <div className="relative">
                <SectionEyebrow>One System. One View. Better Execution.</SectionEyebrow>
                <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
                  The central operating{' '}
                  <span className="text-[#d42b92]">layer</span>
                </h2>
                <p className="mt-5 text-white/70 text-sm sm:text-base lg:text-lg leading-relaxed max-w-2xl">
                  InnovPulse acts as a central operating layer, bringing clarity to how priorities
                  are set, owned, executed and reviewed across the organisation.
                </p>
                <p className="mt-8 text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight text-white">
                  Align. Execute. Measure. Improve.
                </p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#d42b92]/50 bg-[#d42b92]/15 px-6 py-3 text-sm font-medium text-white hover:bg-[#d42b92] hover:border-[#d42b92] transition-colors"
                >
                  Talk to InnovHer <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
